import hasDiscountAvailability from "./hasDiscountAvailability";
import hasSessionTypeForFilter from "./hasSessionTypeForFilter";
import { RestaurantDetailsById } from "../../model/restaurants/byId";
import sortOptions, { type SortOption } from "../../model/sort/options";
import { type Time } from "../../model/times/model";
import {
  type ByRestaurantId,
  type DiscountAvailabilityById,
} from "../../model/types";
import type { NonEmptyArray } from "../../types";
import hasFetchedAvailability from "../store/hasFetchedAvailability";

/**
 * In week view, rank sorts and nearest keep sold-out First Tables. Availability
 * sort only keeps restaurants with a bookable First Table for this party size.
 */
const includesSoldOutRestaurants = (sort: SortOption): boolean => {
  switch (sort) {
    case sortOptions.FEATURED_NEW_TOP:
    case sortOptions.NEWEST:
    case sortOptions.RATING:
    case sortOptions.RANKING:
    case sortOptions.NEAREST:
      return true;
    case sortOptions.AVAILABILITY:
      return false;
  }
};

export default function shouldShowDiscountSection(
  regularOnly: boolean,
  restaurantsById: RestaurantDetailsById,
  hasLastTable: boolean,
  discountAvailabilityById: DiscountAvailabilityById,
  date: string,
  sessions: SessionType[],
  timesIsSet: boolean,
  startTime: Time,
  endTime: Time,
  discountDatesToShowById: ByRestaurantId<NonEmptyArray<string>>,
  discountOnly: boolean | null,
  restaurantId: number,
  isSingleMode: boolean,
  loading: boolean,
  skipInitialFirebaseFetch: boolean,
  people?: number,
  sort?: SortOption,
  filtersApplied?: boolean,
) {
  if (regularOnly) {
    return false;
  }

  const restaurant = restaurantsById[restaurantId];

  if (!restaurant) {
    return false;
  }

  const firstTableSessionTypes = restaurant.firstTableSessionTypes || [];

  // only consider the last table session if the `hasLastTable` is filter is active
  if (hasLastTable) {
    const hasLastTableSession = firstTableSessionTypes.includes("dinner2");

    if (!hasLastTableSession) {
      return false;
    }

    const hasLastTableAvailability = hasDiscountAvailability(
      discountAvailabilityById,
      date,
      restaurantId,
      timesIsSet,
      startTime,
      endTime,
      people,
      "Dinner2",
    );
    const restaurantAvailability = discountAvailabilityById[restaurantId];
    const hasFetched = hasFetchedAvailability(date, restaurantAvailability);
    const isDiscountFetchPending =
      loading || (skipInitialFirebaseFetch && !hasFetched);

    return hasLastTableAvailability || (!hasFetched && isDiscountFetchPending);
  }

  const hasSession = sessions.some((session) =>
    hasSessionTypeForFilter(restaurant.firstTableSessionTypes, session),
  );

  if (!hasSession) {
    return false;
  }

  // always show the discount section in single mode (i.e. when selected on the map)
  if (isSingleMode) {
    return true;
  }

  const hasAvailability = hasDiscountAvailability(
    discountAvailabilityById,
    date,
    restaurantId,
    timesIsSet,
    startTime,
    endTime,
    people,
  );
  const restaurantAvailability = discountAvailabilityById[restaurantId];
  const hasFetched = hasFetchedAvailability(date, restaurantAvailability);
  const isDiscountFetchPending =
    loading || (skipInitialFirebaseFetch && !hasFetched);

  if (timesIsSet) {
    return hasAvailability || (!hasFetched && isDiscountFetchPending);
  }

  // filters mean the diner wants a bookable table, so sold out is never kept
  if (
    date === "week" &&
    (sort === undefined ||
      (!filtersApplied && includesSoldOutRestaurants(sort)))
  ) {
    return true;
  }

  const discountDatesToShow: ReadonlyArray<string> =
    discountDatesToShowById[restaurantId] ?? [];
  const outOfRangeDateSelected =
    date !== "week" && !discountDatesToShow.includes(date); // show the "only available up to # days ahead" when a day beyond the discountDatesToShow is selected

  return (
    (outOfRangeDateSelected && !discountOnly) ||
    hasAvailability ||
    (!hasFetched && isDiscountFetchPending)
  );
}
